const SESSION_KEY = 'session';
const ACCESS_TOKEN_KEY = 'accessToken';
const REFRESH_TOKEN_KEY = 'refreshToken';

export const decodeJwtPayload = (token) => {
  if (!token || typeof token !== 'string') return null;

  try {
    const parts = token.split('.');
    if (parts.length < 2) return null;

    let base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    while (base64.length % 4 !== 0) {
      base64 += '=';
    }

    if (typeof atob === 'function') {
      const decodedStr = atob(base64);
      try {
        const percentEncoded = decodedStr
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('');
        return JSON.parse(decodeURIComponent(percentEncoded));
      } catch {
        return JSON.parse(decodedStr);
      }
    } else if (typeof Buffer !== 'undefined') {
      return JSON.parse(Buffer.from(base64, 'base64').toString('utf8'));
    }
  } catch {
    return null;
  }
  return null;
};

export const extractRoleFromToken = (token) => {
  const payload = decodeJwtPayload(token);
  const role = payload?.user_role || payload?.roleName;
  return role ? String(role).trim().toLowerCase() : null;
};

const safeParseJson = (value) => {
  if (!value) return null;

  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

const readLegacyToken = (key) => {
  try {
    const value = localStorage.getItem(key);
    return value && value !== 'null' && value !== 'undefined' ? value : null;
  } catch {
    return null;
  }
};

export const readPersistedSession = () => {
  try {
    const session = safeParseJson(localStorage.getItem(SESSION_KEY));
    if (session && typeof session === 'object') {
      const token = session.accessToken || session.user?.accessToken;
      const tokenRole = extractRoleFromToken(token);

      if (tokenRole && session.user && typeof session.user === 'object') {
        const currentRole = session.user.role ? String(session.user.role).trim().toLowerCase() : null;
        if (currentRole !== tokenRole) {
          session.user = {
            ...session.user,
            role: tokenRole
          };
          try {
            localStorage.setItem(SESSION_KEY, JSON.stringify(session));
          } catch {
            // Ignore storage failure
          }
        }
      }
      return session;
    }
  } catch {
    // Fall through to legacy keys.
  }

  const accessToken = readLegacyToken(ACCESS_TOKEN_KEY);
  const refreshToken = readLegacyToken(REFRESH_TOKEN_KEY);

  if (!accessToken && !refreshToken) {
    return null;
  }

  const tokenRole = extractRoleFromToken(accessToken);

  return {
    accessToken,
    refreshToken,
    user: tokenRole ? { role: tokenRole } : null
  };
};

export const getPersistedAuthState = () => {
  const session = readPersistedSession();
  const token = session?.accessToken || null;
  const tokenRole = extractRoleFromToken(token);

  let user = session?.user || null;
  if (user && tokenRole) {
    const currentRole = user.role ? String(user.role).trim().toLowerCase() : null;
    if (currentRole !== tokenRole) {
      user = {
        ...user,
        role: tokenRole
      };
    }
  }

  return {
    user,
    accessToken: token,
    refreshToken: session?.refreshToken || null,
    isAuthenticated: Boolean(token)
  };
};

export const writePersistedSession = (sessionData) => {
  try {
    if (!sessionData || typeof sessionData !== 'object') {
      return;
    }

    const accessToken = sessionData.accessToken ?? null;
    const refreshToken = sessionData.refreshToken ?? null;
    const tokenRole = extractRoleFromToken(accessToken);

    let user = sessionData.user ?? null;
    if (user && typeof user === 'object') {
      const normalizedRole = tokenRole || (user.role ? String(user.role).trim().toLowerCase() : null);
      user = {
        ...user,
        ...(normalizedRole ? { role: normalizedRole } : {}),
        ...(accessToken ? { accessToken } : {}),
        ...(refreshToken ? { refreshToken } : {})
      };
    }

    const normalizedSession = {
      user,
      accessToken,
      refreshToken
    };

    localStorage.setItem(SESSION_KEY, JSON.stringify(normalizedSession));

    if (normalizedSession.accessToken) {
      localStorage.setItem(ACCESS_TOKEN_KEY, normalizedSession.accessToken);
    } else {
      localStorage.removeItem(ACCESS_TOKEN_KEY);
    }

    if (normalizedSession.refreshToken) {
      localStorage.setItem(REFRESH_TOKEN_KEY, normalizedSession.refreshToken);
    } else {
      localStorage.removeItem(REFRESH_TOKEN_KEY);
    }

    broadcastAuthEvent('SESSION_UPDATED', normalizedSession);
  } catch {
    // Ignore storage failures so auth state can still exist in memory.
  }
};

export const clearPersistedSession = () => {
  try {
    localStorage.removeItem(SESSION_KEY);
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    broadcastAuthEvent('SESSION_CLEARED');
  } catch {
    // Ignore storage failures during logout.
  }
};

export const AUTH_CHANNEL_NAME = 'pmpi_auth_sync';

export const CURRENT_TAB_ID =
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `tab_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;

let sharedBroadcastChannel = null;

export const getBroadcastChannel = () => {
  if (typeof window !== 'undefined' && typeof window.BroadcastChannel === 'function') {
    if (!sharedBroadcastChannel) {
      try {
        sharedBroadcastChannel = new BroadcastChannel(AUTH_CHANNEL_NAME);
      } catch {
        sharedBroadcastChannel = null;
      }
    }
    return sharedBroadcastChannel;
  }
  return null;
};

export const closeBroadcastChannel = () => {
  if (sharedBroadcastChannel) {
    try {
      sharedBroadcastChannel.close();
    } catch {
      // ignore
    }
    sharedBroadcastChannel = null;
  }
};

export const broadcastAuthEvent = (type, payload = null) => {
  try {
    const channel = getBroadcastChannel();
    if (channel) {
      channel.postMessage({
        type,
        payload,
        timestamp: Date.now(),
        tabId: CURRENT_TAB_ID
      });
    }
  } catch {
    // Ignore broadcast errors
  }
};

export const subscribeToAuthChanges = (onAuthChange) => {
  if (typeof window === 'undefined' || typeof onAuthChange !== 'function') {
    return () => {};
  }

  let lastAuthEventTs = 0;
  let lastEmittedType = null;
  let lastEmittedState = {
    accessToken: null,
    refreshToken: null
  };

  const handleStorage = (event) => {
    if (
      event.key === SESSION_KEY ||
      event.key === ACCESS_TOKEN_KEY ||
      event.key === REFRESH_TOKEN_KEY ||
      event.key === null
    ) {
      const persisted = getPersistedAuthState();

      if (persisted.isAuthenticated) {
        if (
          lastEmittedType === 'SESSION_UPDATED' &&
          persisted.accessToken === lastEmittedState.accessToken &&
          persisted.refreshToken === lastEmittedState.refreshToken
        ) {
          return;
        }

        lastEmittedType = 'SESSION_UPDATED';
        lastEmittedState = {
          accessToken: persisted.accessToken,
          refreshToken: persisted.refreshToken
        };

        onAuthChange('SESSION_UPDATED', persisted);
      } else {
        if (lastEmittedType === 'SESSION_CLEARED') {
          return;
        }

        lastEmittedType = 'SESSION_CLEARED';
        lastEmittedState = {
          accessToken: null,
          refreshToken: null
        };

        onAuthChange('SESSION_CLEARED', persisted);
      }
    }
  };

  const channel = getBroadcastChannel();
  const handleMessage = (event) => {
    const { type, timestamp, tabId } = event?.data || {};

    // Ignore invalid, duplicate, or stale timestamps
    if (!type || !timestamp || timestamp <= lastAuthEventTs) return;

    // Ignore self-originated events
    if (tabId && tabId === CURRENT_TAB_ID) return;

    lastAuthEventTs = timestamp;
    const persisted = getPersistedAuthState();

    if (type === 'SESSION_UPDATED' && persisted.isAuthenticated) {
      if (
        lastEmittedType === 'SESSION_UPDATED' &&
        persisted.accessToken === lastEmittedState.accessToken &&
        persisted.refreshToken === lastEmittedState.refreshToken
      ) {
        return;
      }

      lastEmittedType = 'SESSION_UPDATED';
      lastEmittedState = {
        accessToken: persisted.accessToken,
        refreshToken: persisted.refreshToken
      };

      onAuthChange(type, persisted);
    } else if (type === 'SESSION_CLEARED') {
      if (lastEmittedType === 'SESSION_CLEARED') {
        return;
      }

      lastEmittedType = 'SESSION_CLEARED';
      lastEmittedState = {
        accessToken: null,
        refreshToken: null
      };

      onAuthChange(type, persisted);
    }
  };

  window.addEventListener('storage', handleStorage);
  if (channel) {
    channel.addEventListener('message', handleMessage);
  }

  return () => {
    window.removeEventListener('storage', handleStorage);
    if (channel) {
      channel.removeEventListener('message', handleMessage);
    }
  };
};

export const getPersistedAccessToken = () => getPersistedAuthState().accessToken;

